import { Spacing } from '@persona-id/design-tokens';
import { sum } from 'lodash';
import { useDispatch } from 'react-redux';
import { change } from 'redux-form';
import styled from 'styled-components';

import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import {
  WORKFLOW_STEP_CARD_HEIGHT,
  WORKFLOW_STEP_CARD_WIDTH,
  WorkflowStepCard,
} from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree';
import InputWithHoverEditButtonField from 'dashboard/components/FormControls/InputWithHoverEditButtonField';
import StepFlow from 'dashboard/components/StepFlow';
import { StepFlowVerticalLine } from 'dashboard/components/StepFlow/StepFlowElements';
import {
  WorkflowStep,
  WorkflowStepAll,
  WorkflowStepParallel,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { Label } from 'design-system/components/Text';

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;

// scaled down size of preview tree
const PREVIEW_SCALE = 0.6;
// non-scaled padding for preview container
const PREVIEW_PADDING = 16;
// non-scaled padding for StepFlow
const STEP_CARD_PADDING = 16;
// static padding for the container that needs to be added back
// in when determining the dimensions of the preview container
const STATIC_PADDING = PREVIEW_PADDING + STEP_CARD_PADDING;
// connectors between action steps
const CONNECTOR_HEIGHT = 16;
// parallel steps have 4 connectors - 2 vertical ones bookending the parallel portion,
// and two additional sets of curved lines which make up the branches.
const PARALLEL_CONNECTOR_HEIGHT = CONNECTOR_HEIGHT * 4;
// padding + title height + margin between title and preview wrapper
const HEADER_HEIGHT = 44;

const getTreePreviewDimens = (
  steps: WorkflowStep[],
): { treePreviewWidth: number; treePreviewHeight: number } => {
  const parallelSteps = steps.filter(
    (step): step is WorkflowStepParallel => step.type === WorkflowStepType.Parallel,
  );
  const treePreviewWidth =
    (parallelSteps.length > 0
      ? // parallel step width is 2 card widths + 16px padding between
        WORKFLOW_STEP_CARD_WIDTH * 2 + STEP_CARD_PADDING
      : WORKFLOW_STEP_CARD_WIDTH) * PREVIEW_SCALE;
  // Sum the parallel steps' longest branch lengths to get the full additional height of the action sequence tree
  const nestedStepCount = sum(
    parallelSteps.map((parallelStep) =>
      Math.max(...parallelStep.branches.map((branch) => branch.length)),
    ),
  );
  const linearStepCount = steps.length - parallelSteps.length + nestedStepCount;
  const linearStepHeight = (WORKFLOW_STEP_CARD_HEIGHT + CONNECTOR_HEIGHT) * linearStepCount;
  const parallelStepHeight =
    (WORKFLOW_STEP_CARD_HEIGHT + PARALLEL_CONNECTOR_HEIGHT) * parallelSteps.length;
  const treePreviewHeight =
    // Subtract a connector height to account for the terminal step
    (linearStepHeight + parallelStepHeight - CONNECTOR_HEIGHT) * PREVIEW_SCALE;
  return { treePreviewWidth, treePreviewHeight };
};

const ContentWrapper = styled.div<{
  width: number;
  height: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${Spacing[1]};
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
`;

const Title = styled.div`
  width: 100%;
  text-align: start;
`;

const StyledHoverEditField = styled(InputWithHoverEditButtonField)`
  & > div {
    gap: 2px;
  }
  svg {
    height: 13px;
    width: 13px;
  }
`;

const PreviewLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 20px;
`;

const PreviewContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  padding: ${PREVIEW_PADDING}px;
  transform-origin: center top;
  transform: scale(${PREVIEW_SCALE});
  margin-top: ${Spacing[3]};
  // remove top margin from StepFlow
  div:first-child {
    margin-top: 0px;
  }
`;

const VerticalLineContainer = styled.div`
  position: relative;
  height: ${CONNECTOR_HEIGHT}px;
  width: ${WORKFLOW_STEP_CARD_WIDTH}px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

interface ActionSequencePreviewProps {
  title: string;
  steps: WorkflowStep[];
  workflowVersionId: string;
  editable: boolean;
}

const ActionSequencePreview = ({
  title,
  steps,
  workflowVersionId,
  editable,
}: ActionSequencePreviewProps) => {
  const dispatch = useDispatch();
  const isITEInternal = useIsITEInternal();
  const renderStep = (currentStep: WorkflowStepAll) => {
    switch (currentStep.type) {
      case WorkflowStepType.Action:
      case WorkflowStepType.Wait:
      case WorkflowStepType.Conditional:
      case WorkflowStepType.Loop:
      case WorkflowStepType.Parallel:
      case WorkflowStepType.Placeholder: {
        return (
          <WorkflowStepCard
            type={currentStep.type}
            token={currentStep.token}
            isInitial={currentStep.token === steps[0].token}
            isLast={currentStep.token === steps[steps.length - 1].token}
            name={currentStep.name}
            focused={false}
            hasError={false}
          />
        );
      }
      default:
        return null;
    }
  };

  // Not needed, since action sequences do not contain conditional steps
  const renderBranchRootConnector = (_branchRootStep: WorkflowStep, _branchIndex: number) => <></>;
  const renderConnector = (_previousStep: WorkflowStepAll, nextStep: WorkflowStepAll) =>
    nextStep != null ? (
      <VerticalLineContainer>
        <StepFlowVerticalLine width={WORKFLOW_STEP_CARD_WIDTH} />
      </VerticalLineContainer>
    ) : (
      <></>
    );

  const { treePreviewWidth, treePreviewHeight } = getTreePreviewDimens(steps);
  const path = `workflowVersions.${workflowVersionId}.attributes.workflow-name`;
  const canEditWorkflowName = isITEInternal && editable;

  return (
    <ContentWrapper
      width={treePreviewWidth + STATIC_PADDING}
      height={treePreviewHeight + STATIC_PADDING + HEADER_HEIGHT}
    >
      <Title>
        <StyledHoverEditField
          size='sm'
          id={path}
          fieldName={path}
          displayElement={<PreviewLabel size='sm' />}
          displayPlaceholder={title}
          placeholder={title}
          disabled={!canEditWorkflowName}
          iconVisible
          onBlur={(value: string) => {
            dispatch(change(form, path, value));
          }}
        />
      </Title>
      <PreviewContainer>
        <StepFlow
          innerRef={null}
          steps={steps}
          withMinWidth={false}
          stepContentWidth={WORKFLOW_STEP_CARD_WIDTH}
          renderStep={renderStep}
          renderConnector={renderConnector}
          renderBranchRootConnector={renderBranchRootConnector}
        />
      </PreviewContainer>
    </ContentWrapper>
  );
};

export default ActionSequencePreview;
